const ErrorLevel = {
    None: 0,
    Warning: 1,
    Error: 2
};
const atomizeData = (data)=>({
        data,
        error: undefined
    });
function atom(cb, defaultValue) {
    return (data)=>{
        try {
            return atomizeData(cb(data));
        } catch (error) {
            if (defaultValue) {
                return {
                    error,
                    data: defaultValue
                };
            }
            return {
                error,
                data: undefined
            };
        }
    };
}
atom.from = atomizeData;
function dataComposer(data) {
    const newObj = {};
    const errors = {};
    for(const key in data){
        if (!Object.prototype.hasOwnProperty.call(data, key)) continue;
        const atom = data[key];
        if (atom.error) {
            errors[key] = atom.error;
        }
        newObj[key] = atom.data;
    }
    return {
        data: newObj,
        errors: errors
    };
}

class TimeSpan {
    milliseconds() {
        if (this.unit === "ms") {
            return this.value;
        }
        if (this.unit === "s") {
            return this.value * 1000;
        }
        if (this.unit === "m") {
            return this.value * 1000 * 60;
        }
        if (this.unit === "h") {
            return this.value * 1000 * 60 * 60;
        }
        if (this.unit === "d") {
            return this.value * 1000 * 60 * 60 * 24;
        }
        return this.value * 1000 * 60 * 60 * 24 * 7;
    }
    seconds() {
        return this.milliseconds() / 1000;
    }
    transform(x) {
        return new TimeSpan(Math.round(this.milliseconds() * x), "ms");
    }
    constructor(value, unit){
        this.value = value;
        this.unit = unit;
    }
}

const dedupeArray = (arr)=>{
    const unique = new Set(arr);
    return Array.from(unique);
};
const chunk = (arr, size)=>{
    const result = [];
    for(let i = 0; i < arr.length; i += size){
        result.push(arr.slice(i, i + size));
    }
    return result;
};
function dedupeBy(array, callback) {
    const seen = new Set();
    return array.filter((value)=>{
        const key = callback(value, array.indexOf(value));
        if (seen.has(key)) {
            return false;
        }
        seen.add(key);
        return true;
    });
}

const Logger = ({ enabled = true, prefix = "[FREND]" })=>{
    return {
        log: (...args)=>{
            if (enabled) {
                console.log(prefix, ...args);
            }
        },
        warn: (...args)=>{
            if (enabled) {
                console.warn(prefix, ...args);
            }
        },
        error: (...args)=>{
            if (enabled) {
                console.error(prefix, ...args);
            }
        },
        info: (...args)=>{
            if (enabled) {
                console.info(prefix, ...args);
            }
        }
    };
};

/**
 * This is a utility function that allows you to omit keys from an object.
 * Produces an immutable object.
 * Performs omit at the first level only.
 */ const omit = (obj, keys)=>{
    // Path for objects only. That includes array
    if (typeof obj !== "object" || Array.isArray(obj)) {
        throw new Error(`omit only accepts objects, got ${Array.isArray(obj) ? "Array" : typeof obj}`);
    }
    const referentialClone = structuredClone(obj);
    keys.forEach((key)=>{
        if (Object.hasOwn(referentialClone, key)) {
            delete referentialClone[key];
        }
    });
    return referentialClone;
};
/**
 * Omits falsy values from an object.
 * Produces an immutable object.
 * Performs omit at the first level only.
 */ const omitFalsy = (obj)=>{
    return Object.fromEntries(Object.entries(structuredClone(obj)).filter(([_, v])=>!!v));
};
/**
 * Omits nullish values from an object. Keeps `0` and `BigInt(0n)`
 * Produces an immutable object.
 * Performs omit at the first level only.
 */ const omitNullish = (obj)=>{
    return Object.fromEntries(Object.entries(structuredClone(obj)).filter(([_, v])=>{
        if (typeof v === "number") return true;
        return !!v;
    }));
};
/**
 * Omits undefined values from an object by the predicate provided.
 * Produces an immutable object.
 * Performs omit at the first level only. Calls the predicate at first level only.
 */ const omitBy = (obj, predicate)=>{
    return Object.fromEntries(Object.entries(structuredClone(obj)).filter(([key, value])=>!predicate(value, key)));
};
/**
 * Picks keys from an object.
 * Produces an immutable object.
 * Performs pick at the first level only. Calls the predicate at first level only.
 */ const pickBy = (obj, predicate)=>{
    return Object.fromEntries(Object.entries(structuredClone(obj)).filter(([key, value])=>predicate(value, key)));
};
/**
 * Checks if an object or array is empty.
 */ const isEmpty = (obj)=>{
    if (Array.isArray(obj)) {
        return obj.length === 0;
    }
    return Object.keys(obj).length === 0;
};
/**
 * Safely picks a value from a deeply nested object using a string path.
 * Supports both property access and array indexing.
 *
 * @param obj The source object to pick from
 * @param path The path to the desired value (e.g. "user.address.street" or "users.[0].name")
 * @param defaultValue Optional default value if the path doesn't exist
 * @returns The value at the specified path or undefined/defaultValue if not found
 */ function safePick(obj, path, defaultValue) {
    // Handle empty cases
    if (!obj || !path) {
        return defaultValue;
    }
    try {
        // Split the path by dots while preserving array notation
        const segments = splitPath(path);
        let current = obj;
        for (const segment of segments){
            // Check if we're accessing an array element
            if (segment.includes("[") && segment.includes("]")) {
                const [arrName, indexStr] = segment.split("[");
                if (!indexStr) return defaultValue;
                const index = parseInt(indexStr.replace("]", ""), 10);
                if (!arrName) return defaultValue;
                // Get the array first
                current = current[arrName];
                // Then access the element
                if (current === undefined || current === null || !Array.isArray(current) || index >= current.length) {
                    return defaultValue;
                }
                current = current[index];
            } else {
                // Regular property access
                current = current[segment];
            }
            // If we hit undefined or null along the way, return the default value
            if (current === undefined || current === null) {
                return defaultValue;
            }
        }
        return current;
    } catch (error) {
        return defaultValue;
    }
}
/**
 * Helper function to split a path string into segments, handling array notation correctly.
 */ function splitPath(path) {
    const result = [];
    let current = "";
    let inArrayNotation = false;
    for(let i = 0; i < path.length; i++){
        const char = path[i];
        if (char === "." && !inArrayNotation) {
            if (current) {
                result.push(current);
                current = "";
            }
        } else {
            if (char === "[") {
                inArrayNotation = true;
            } else if (char === "]") {
                inArrayNotation = false;
            }
            current += char;
        }
    }
    if (current) {
        result.push(current);
    }
    return result;
}

/**
 * Web compatible sha256 implementation
 */ const sha256 = async (input)=>{
    const encoder = new TextEncoder();
    const data = encoder.encode(input);
    const hash = await crypto.subtle.digest("SHA-256", data);
    return Array.from(new Uint8Array(hash)).map((b)=>b.toString(16).padStart(2, "0")).join("");
};

/**
 * Converts a string to a slug. Removes special characters, converts to lowercase, and replaces spaces with hyphens.
 */ function slugify(str, dictionary = defaultDictionary) {
    str = str.replace(/^\s+|\s+$/g, ""); // trim leading/trailing white space
    str = str.toLowerCase(); // convert string to lowercase
    // replace special characters
    for (const [key, value] of Object.entries(dictionary)){
        str = str.replace(new RegExp(key, "g"), value);
    }
    str = str.replace(/[^a-z0-9 -]/g, "") // remove any non-alphanumeric characters
    .replace(/\s+/g, "-") // replace spaces with hyphens
    .replace(/-+/g, "-"); // remove consecutive hyphens
    return str;
}
const defaultDictionary = {
    æ: "ae",
    ø: "o",
    å: "a"
};

function suppressLogMatching(message) {
    const globalConsoleError = console.error;
    const globalConsoleWarn = console.warn;
    const globalConsoleInfo = console.info;
    const globalConsoleLog = console.log;
    // Suppress logs including the message
    console.error = (...args)=>{
        if (args.some((arg)=>typeof arg === "string" && arg.includes(message))) {
            return;
        }
        globalConsoleError(...args);
    };
    console.log = (...args)=>{
        if (args.some((arg)=>typeof arg === "string" && arg.includes(message))) {
            return;
        }
        globalConsoleLog(...args);
    };
    console.warn = (...args)=>{
        if (args.some((arg)=>typeof arg === "string" && arg.includes(message))) {
            return;
        }
        globalConsoleWarn(...args);
    };
    console.info = (...args)=>{
        if (args.some((arg)=>typeof arg === "string" && arg.includes(message))) {
            return;
        }
        globalConsoleInfo(...args);
    };
}

async function tryCatch(promise) {
    try {
        const data = await promise;
        return [
            data,
            null
        ];
    } catch (error) {
        return [
            null,
            error
        ];
    }
}

const simpleDb = {
    json: [
        "json"
    ],
    javascript: [
        "js"
    ],
    image: [
        "png",
        "jpg",
        "jpeg",
        "svg",
        "webp",
        "gif",
        "avif"
    ],
    video: [
        "mp4",
        "webm",
        "ogg",
        "mov"
    ],
    font: [
        "ttf",
        "woff",
        "woff2",
        "otf",
        "eot"
    ],
    audio: [
        "mp3",
        "wav",
        "ogg",
        "flac"
    ]
};
const reversedDb = new Map(Object.entries(simpleDb).flatMap(([type, extension])=>extension.map((ext)=>[
            ext,
            type
        ])));
const typeByExtensionSimple = (extension)=>{
    const ext = extension.split(".").pop();
    if (ext) {
        return reversedDb.get(ext) || null;
    }
    return null;
};

const addTrailingSlash = (path)=>{
    return path.endsWith("/") ? path : `${path}/`;
};
const removeTrailingSlash = (path)=>{
    return path.endsWith("/") ? path.slice(0, -1) : path;
};
const addLeadingSlash = (path)=>{
    return path.startsWith("/") ? path : `/${path}`;
};
const removeLeadingSlash = (path)=>{
    return path.startsWith("/") ? path.slice(1) : path;
};
class BaseURL extends URL {
    constructor(input, base){
        super(base);
        this.pathname = addTrailingSlash(removeLeadingSlash(this.pathname)) + removeLeadingSlash(removeTrailingSlash(input));
    }
}

class Emitter {
    constructor(){
        this.subscribers = new Set();
        this.emit = (data)=>{
            this.subscribers.forEach((subscriber)=>subscriber(data));
        };
        this.subscribe = (subscriber)=>{
            this.subscribers.add(subscriber);
            return ()=>{
                this.subscribers.delete(subscriber);
            };
        };
    }
}

export { BaseURL, Emitter, ErrorLevel, Logger, TimeSpan, addLeadingSlash, addTrailingSlash, atom, atomizeData, chunk, dataComposer, dedupeArray, dedupeBy, isEmpty, omit, omitBy, omitFalsy, omitNullish, pickBy, removeLeadingSlash, removeTrailingSlash, safePick, sha256, slugify, suppressLogMatching, tryCatch, typeByExtensionSimple };
